import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useCreateBoard } from "@/hooks/useBoard"
import { useToast } from "@/hooks/useToast"
import { Loader2 } from "lucide-react"

interface CreateBoardDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (boardId: string) => void
}

export function CreateBoardDialog({ open, onOpenChange, onSuccess }: CreateBoardDialogProps) {
  const { toast } = useToast()
  const createBoard = useCreateBoard()
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setName("")
      setDescription("")
    }
  }, [open])

  const handleCreate = async () => {
    if (!name.trim()) {
      toast({
        description: "Board name is required",
        variant: "destructive",
      })
      return
    }

    try {
      await createBoard.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      })

      const boardName = name.trim()
      setName("")
      setDescription("")
      onOpenChange(false)

      toast({
        description: "Board created successfully",
      })

      // Call onSuccess callback if provided, passing the board name
      // The parent component can use this to find and select the board
      // React Query will automatically refetch the boards list
      if (onSuccess) {
        onSuccess(boardName)
      }
    } catch (error: any) {
      toast({
        description: error?.response?.data?.error || "Failed to create board",
        variant: "destructive",
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Board</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter board name"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter board description"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={createBoard.isPending}>
            {createBoard.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
